import { ReactElement } from "react";
import styled from "styled-components";
import InlineHtml from "../InlineHtml";

type Align = "left" | "center" | "right";
const textAlign = (alignment?: string): Align =>
  alignment === "center" || alignment === "right" ? alignment : "left";

/* ---------------------------------- header ---------------------------------- */

interface HeaderData {
  text?: string;
  level?: number;
  alignment?: string;
}

// Sizes clamp at h3 — EditorJS levels go to 6 but anything below 18px stops
// reading as a heading on a phone.
const HEADER_SIZES: Record<number, string> = { 1: "26px", 2: "21px", 3: "18px" };

const StyledHeading = styled.h2<{ $level: number; $align: Align }>`
  margin: 0;
  font-size: ${({ $level }) => HEADER_SIZES[Math.min($level, 3)] ?? "18px"};
  font-weight: 700;
  line-height: 1.25;
  text-align: ${({ $align }) => $align};
  color: var(--app-color-text, #041723);
  text-wrap: balance;

  mark,
  .cdx-marker {
    background: rgba(var(--app-color-secondary-rgb, 254, 207, 72), 0.35);
    color: inherit;
    padding: 0 2px;
    border-radius: 3px;
  }
`;

export const HeaderBlock = ({ data }: { data: HeaderData }): ReactElement => {
  const level = Math.min(Math.max(Number(data.level) || 2, 1), 6);
  return (
    <StyledHeading as={`h${level}` as "h2"} $level={level} $align={textAlign(data.alignment)}>
      <InlineHtml html={data.text} />
    </StyledHeading>
  );
};

/* --------------------------------- paragraph --------------------------------- */

interface ParagraphData {
  text?: string;
  alignment?: string;
}

const StyledParagraph = styled.p<{ $align: Align }>`
  margin: 0;
  font-size: 16px;
  line-height: 1.6;
  text-align: ${({ $align }) => $align};
  color: var(--app-color-text, #041723);
  overflow-wrap: anywhere;

  a {
    color: var(--app-color-accent, #2db6b4);
    text-decoration: underline;
  }
  mark,
  .cdx-marker {
    background: rgba(var(--app-color-secondary-rgb, 254, 207, 72), 0.35);
    color: inherit;
    padding: 0 2px;
    border-radius: 3px;
  }
`;

export const ParagraphBlock = ({ data }: { data: ParagraphData }): ReactElement => (
  <StyledParagraph $align={textAlign(data.alignment)}>
    <InlineHtml html={data.text} />
  </StyledParagraph>
);

/* ------------------------------------ list ----------------------------------- */

interface ListData {
  style?: string;
  items?: unknown[];
}

// @editorjs/list stores plain strings; newer list tools may nest {content, items} —
// tolerate both (same defensiveness as the legacy PDF walker).
export const listItemText = (item: unknown): string =>
  typeof item === "string" ? item : String((item as { content?: string })?.content ?? "");

const StyledList = styled.ul`
  margin: 0;
  padding-left: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 16px;
  line-height: 1.6;
  color: var(--app-color-text, #041723);

  li::marker {
    color: var(--app-color-accent, #2db6b4);
  }
  a {
    color: var(--app-color-accent, #2db6b4);
    text-decoration: underline;
  }
  mark,
  .cdx-marker {
    background: rgba(var(--app-color-secondary-rgb, 254, 207, 72), 0.35);
    color: inherit;
    padding: 0 2px;
    border-radius: 3px;
  }
`;

export const ListBlock = ({ data }: { data: ListData }): ReactElement => (
  <StyledList as={data.style === "ordered" ? "ol" : "ul"}>
    {(data.items ?? []).map((item, i) => (
      <li key={i}>
        <InlineHtml html={listItemText(item)} />
      </li>
    ))}
  </StyledList>
);
